import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Camera, Edit3, Save, X, Plus, Trash2, 
  Loader2, AlertCircle, ChevronDown, Check, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { mealAPI, logFoodAPI, mealPlanAPI } from '../utils/api';
import { toast } from 'sonner';
import { resolveLoggingDate, formatFriendlyDate } from '../utils/dateUtils';
import { useToday } from '../hooks/useToday';
import { applyUnitChange, computeStructuredTotals } from '../utils/customMealMath';
import { isRebuiltPersistedMeal } from './detectRebuiltMeal';
import { useMealImageSrc } from '../components/MealImage';

/**
 * CustomMealView — unified detail view for custom meals (multi-ingredient) and logged foods.
 * 
 * Behavior:
 * - Multi-ingredient meals: show ingredients, macros, Edit button for modifying name/macros/ingredients
 * - Single-food logs: show food name + macros only, no Edit button (nothing meaningful to edit)
 * - In edit mode: ingredients with food_id show real unit dropdown (USDA-backed); text-only ingredients use text input
 */
export default function GroupedMealView() {
  const { mealId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const planId = searchParams.get('planId');
  const returnTo = searchParams.get('return') || '/progress';
  const navStatus = searchParams.get('status') || '';
  const contextMealType = searchParams.get('type') || '';
  // Reactive LOCAL current day (same source as Home). `?current=1` marks a live current-day
  // "Log Again"; otherwise an explicit ?date= (fixed calendar/history selection) is preserved.
  const liveToday = useToday();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meal, setMeal] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logging, setLogging] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [mealName, setMealName] = useState('');
  const [macros, setMacros] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  // Display-only resolution of a private AI-photo marker ("/api/ai-log/photo/{meal_id}") via the
  // authenticated resolver. `imageUrl` (the marker/original) is kept unchanged for save/log ops so a
  // short-lived signed URL is NEVER persisted. Normal https/data URLs pass through; '' -> placeholder.
  const heroSrc = useMealImageSrc(imageUrl);
  const [ingredients, setIngredients] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Track whether this is a single-food log vs multi-ingredient meal
  const [isSimpleFood, setIsSimpleFood] = useState(false);
  // ITEM-19: authoritative rebuild-backed meal — detected from PERSISTED data (server marker or a
  // component carrying a rebuilt provenance block), NOT from the meal-level workflow source. When true,
  // Edit is metadata-only (name/photo); macros/ingredients are display-only and cannot be locally edited.
  const [isRebuiltMeal, setIsRebuiltMeal] = useState(false);
  // Stable per-visit idempotency key for a rebuild-aware "Log Again" (one mount = one new event).
  const logAgainOpRef = useRef('rblog_' + Math.random().toString(36).slice(2) + Date.now().toString(36));
  
  // Same logging-date rules as MealView:
  //   * a pending/planned saved row (planId, not completed) is completed ONLY
  //     in its own saved date context (?date= = the row's date via planRowDate);
  //   * a COMPLETED saved row is "already logged" — its action is Log Again,
  //     which creates a NEW completed event and never re-updates that row;
  //   * fresh logs / Log Again resolve the logging date: ?current=1 follows the
  //     reactive local day (midnight-safe), a fixed ?date= is preserved.
  // Re-logs from Recents never carry planId (LogFood openGroupedRecent), so
  // they can never masquerade as an already-logged row.
  const rowDate = searchParams.get('date') || liveToday;
  const isPlannedRow = !!(planId && navStatus !== 'completed');
  const isAlreadyLogged = !!(planId && navStatus === 'completed');
  const logDate = isPlannedRow
    ? rowDate
    : resolveLoggingDate({
        explicitDate: searchParams.get('date'),
        currentFlag: searchParams.get('current'),
        liveToday,
        missingMeansCurrent: false,
      });
  const logDateLabel = formatFriendlyDate(logDate);
  const rowDateLabel = formatFriendlyDate(rowDate);

  // Load meal data
  useEffect(() => {
    async function loadMeal() {
      if (!mealId) { setError('No meal ID provided'); setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const response = await mealAPI.getCustomMeal(mealId, planId);
        const data = response.data;
        setMeal(data);
        setMealName(data.name || searchParams.get('name') || 'Custom Meal');
        setImageUrl(data.image_url || '');
        setMacros({
          calories: Math.round(data.calories || parseInt(searchParams.get('calories')) || 0),
          protein: Math.round(data.protein || parseInt(searchParams.get('protein')) || 0),
          carbs: Math.round(data.carbs || parseInt(searchParams.get('carbs')) || 0),
          fat: Math.round(data.fat || parseInt(searchParams.get('fat')) || 0)
        });
        
        // Parse ingredients from multiple possible sources
        var parsedIngredients = [];
        if (data.ingredient_quantities && data.ingredient_quantities.length > 0) {
          // Structured data with individual macros and food_id
          parsedIngredients = data.ingredient_quantities.map(function(ing, idx) {
            return {
              id: idx,
              text: (ing.quantity || 1) + ' ' + (ing.unit || '') + ' ' + (ing.name || ''),
              food_id: ing.food_id || null,
              quantity: ing.quantity || 1,
              unit: ing.unit || 'serving',
              name: ing.name || '',
              calories: ing.calories || 0,
              protein: ing.protein || 0,
              carbs: ing.carbs || 0,
              fat: ing.fat || 0,
              structured: true,
              // ITEM-19: preserve the authoritative rebuilt provenance block verbatim so an
              // Edit/Save (or Log Again) never strips Item-15 nutrition provenance. This block is
              // the source of truth; the flat fields above are derived display values only.
              rebuilt: ing.rebuilt || null,
              // Retain source identifiers / serving data so editing a grouped
              // (AI/basket) meal keeps each component's FatSecret provenance.
              fatsecret_food_id: ing.fatsecret_food_id || ing.food_id || null,
              external_id: ing.external_id || null,
              match_source: ing.match_source || null,
              per_unit: ing.per_unit || null,
              serving_description: ing.serving_description || null,
              brand: ing.brand || null,
              restaurant: ing.restaurant || null,
              // Full FatSecret serving identity — kept so a unit change stays
              // consistent when reopened/edited. Legacy rows lack these (null-safe).
              serving_id: ing.serving_id || null,
              serving_size: ing.serving_size || null,
              measurement_description: ing.measurement_description || null,
              metric_serving_amount: (ing.metric_serving_amount != null ? ing.metric_serving_amount : null),
              metric_serving_unit: ing.metric_serving_unit || null,
            };
          });
        } else if (data.ingredients && data.ingredients.length > 0) {
          parsedIngredients = data.ingredients.map(function(ing, idx) {
            if (typeof ing === 'string') {
              return { id: idx, text: ing, structured: false };
            }
            return {
              id: idx,
              text: (ing.quantity || 1) + ' ' + (ing.unit || '') + ' ' + (ing.name || ''),
              food_id: ing.food_id || null,
              quantity: ing.quantity || 1,
              unit: ing.unit || 'serving',
              name: ing.name || '',
              calories: ing.calories || 0,
              protein: ing.protein || 0,
              carbs: ing.carbs || 0,
              fat: ing.fat || 0,
              structured: !!ing.food_id
            };
          });
        }
        
        setIngredients(parsedIngredients);
        setIsRebuiltMeal(isRebuiltPersistedMeal(data, parsedIngredients));
        // Single-food = 0 or 1 real ingredients
        setIsSimpleFood(parsedIngredients.length <= 1 && !parsedIngredients.some(function(i) { return i.structured; }));
      } catch (err) {
        console.error('Error loading meal:', err);
        // Fallback to URL params
        var urlName = searchParams.get('name');
        var urlCalories = searchParams.get('calories');
        if (urlName && urlCalories) {
          setMealName(urlName);
          setMacros({
            calories: parseInt(urlCalories) || 0,
            protein: parseInt(searchParams.get('protein')) || 0,
            carbs: parseInt(searchParams.get('carbs')) || 0,
            fat: parseInt(searchParams.get('fat')) || 0
          });
          setIsSimpleFood(true);
          setIngredients([]);
        } else {
          setError('Could not load meal details');
        }
      } finally {
        setLoading(false);
      }
    }
    loadMeal();
  }, [mealId, planId, searchParams]);

  // Image upload
  const handleImageUpload = async (e) => {
    var file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      var reader = new FileReader();
      reader.onloadend = function() { setImageUrl(reader.result); setUploadingImage(false); };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploadingImage(false);
      toast.error('Failed to upload image');
    }
  };

  // Macro update
  const updateMacro = function(key, value) {
    setMacros({ ...macros, [key]: Math.max(0, parseInt(value) || 0) });
  };

  // Ingredient text update
  const updateIngredient = function(id, text) {
    setIngredients(ingredients.map(function(ing) {
      return ing.id === id ? { ...ing, text: text } : ing;
    }));
  };

  // Unit change for a structured ingredient — FatSecret-aware (see applyUnitChange).
  // `option` is a full serving_options entry (per-ONE-unit macros + serving
  // identity). We recompute macros = option.macros * quantity and the grouped
  // parent totals; we NEVER relabel without recomputing. Invalid option is ignored.
  const changeIngredientUnit = useCallback(function(id, option) {
    var ing = ingredients.find(function(i) { return i.id === id; });
    if (ing && ing.rebuilt) return;   // ITEM-19: don't legacy-recompute an authoritative rebuilt component
    var result = applyUnitChange(ingredients, id, option);
    setIngredients(result.ingredients);
    setMacros(result.totals);
  }, [ingredients]);

  // Quantity change for structured ingredient — Phase 0: proportional scale
  // from stored macros. Phase 1 (FatSecret) will reintroduce per-quantity
  // server-side recalculation.
  const changeIngredientQuantity = useCallback(function(id, newQty) {
    var ing = ingredients.find(function(i) { return i.id === id; });
    if (!ing || !ing.food_id || newQty <= 0) return;
    // ITEM-19: a persisted rebuilt component's amount is authoritative history. Do NOT locally
    // perUnit/ratio-scale it (needs a rebuild-aware recompute, not legacy math). No-op here.
    if (ing.rebuilt) return;

    var oldQty = ing.quantity || 1;
    var ratio = newQty / oldQty;

    var updatedIngredients = ingredients.map(function(i) {
      if (i.id !== id) return i;
      return {
        ...i,
        quantity: newQty,
        text: newQty + ' ' + i.unit + ' ' + i.name,
        calories: Math.round((i.calories || 0) * ratio),
        protein: Math.round((i.protein || 0) * ratio),
        carbs: Math.round((i.carbs || 0) * ratio),
        fat: Math.round((i.fat || 0) * ratio),
      };
    });
    setIngredients(updatedIngredients);
    setMacros(computeStructuredTotals(updatedIngredients));
  }, [ingredients]);

  // Remove ingredient
  const removeIngredient = function(id) {
    var target = ingredients.find(function(ing) { return ing.id === id; });
    // ITEM-19: a rebuilt component (esp. a derived residual remainder / residual member) must not be
    // independently removed via the legacy editor — that would change residual membership/arithmetic.
    if (target && target.rebuilt) return;
    var remaining = ingredients.filter(function(ing) { return ing.id !== id; });
    setIngredients(remaining);
    if (remaining.some(function(i) { return i.structured; })) {
      setMacros(computeStructuredTotals(remaining));
    }
  };

  // Add new text ingredient
  const addIngredient = function() {
    setIngredients([...ingredients, { id: Date.now(), text: '', structured: false }]);
  };

  // Save changes
  const saveChanges = async function() {
    setSaving(true);
    try {
      // ITEM-19: a rebuild-backed meal is metadata-only here (no rebuild-aware historical recompute yet).
      // Send ONLY name/photo + plan_id; the backend independently rejects any nutrition/ingredient
      // mutation on a rebuild-backed plan, so no fake local macro/ingredient edit is ever offered.
      if (isRebuiltMeal) {
        await mealAPI.updateCustomMeal(mealId, { name: mealName, image_url: imageUrl, plan_id: planId });
        toast.success('Meal updated');
        setEditMode(false);
        return;
      }
      // Rebuild structured ingredient_details from the current (edited) components so
      // the grouped meal's child rows + FatSecret identifiers survive the edit. The
      // parent totals were already recomputed on each qty/remove change; sending both
      // in one update keeps the write atomic.
      var structured = ingredients.filter(function(ing) { return ing.structured || ing.food_id || ing.name; });
      var ingredientDetails = structured.map(function(ing) {
        var comp = {
          name: ing.name || (ing.text || '').trim(),
          food_id: ing.food_id || ing.fatsecret_food_id || null,
          quantity: ing.quantity || 1,
          unit: ing.unit || 'serving',
          calories: Math.round(ing.calories || 0),
          protein: Math.round((ing.protein || 0) * 10) / 10,
          carbs: Math.round((ing.carbs || 0) * 10) / 10,
          fat: Math.round((ing.fat || 0) * 10) / 10,
          fatsecret_food_id: ing.fatsecret_food_id || ing.food_id || null,
          external_id: ing.external_id || null,
          match_source: ing.match_source || null,
          per_unit: ing.per_unit || null,
          serving_description: ing.serving_description || null,
          brand: ing.brand || null,
          restaurant: ing.restaurant || null,
          serving_id: ing.serving_id || null,
          serving_size: ing.serving_size || null,
          measurement_description: ing.measurement_description || null,
          metric_serving_amount: (ing.metric_serving_amount != null ? ing.metric_serving_amount : null),
          metric_serving_unit: ing.metric_serving_unit || null,
        };
        // ITEM-19: carry the authoritative rebuilt provenance block through UNCHANGED (the backend also
        // rejects any attempt to overwrite it on a rebuild-backed meal — belt and suspenders).
        if (ing.rebuilt) comp.rebuilt = ing.rebuilt;
        return comp;
      });
      var payload = {
        name: mealName,
        image_url: imageUrl,
        calories: macros.calories,
        protein: macros.protein,
        carbs: macros.carbs,
        fat: macros.fat,
        ingredients: ingredients.map(function(ing) { return ing.text; }).filter(function(t) { return t.trim(); }),
        plan_id: planId,
      };
      if (ingredientDetails.length > 0) payload.ingredient_details = ingredientDetails;
      await mealAPI.updateCustomMeal(mealId, payload);
      toast.success('Meal updated');
      setEditMode(false);
    } catch (err) {
      console.error('Error saving meal:', err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEdit = function() {
    if (meal) {
      setMealName(meal.name || 'Custom Meal');
      setMacros({
        calories: Math.round(meal.calories || 0),
        protein: Math.round(meal.protein || 0),
        carbs: Math.round(meal.carbs || 0),
        fat: Math.round(meal.fat || 0)
      });
    }
    setEditMode(false);
  };

  // Log meal (new entry) or Log Again
  const handleLogMeal = async function() {
    if (!mealId) return;
    
    // The ONE logging date the CTA shows (see logDate above).
    var targetDate = logDate;

    console.log('[CustomMealView] Logging meal:', {
      date: targetDate,
      meal_id: mealId,
      meal_name: mealName,
      isPlannedRow: isPlannedRow,
      isAlreadyLogged: isAlreadyLogged
    });

    setLogging(true);
    try {
      if (isPlannedRow) {
        // Pending/planned saved row in its own saved date context → complete
        // that row. A COMPLETED row never reaches this status-only path.
        await mealPlanAPI.updateMealPlan(planId, 'completed');
        toast.success('Meal logged!');
      } else if (isRebuiltMeal && planId) {
        // ITEM-19: rebuild-aware "Log Again" — the SERVER clones the authoritative stored snapshots of
        // this owned source plan into a fresh rebuild-backed logged meal. Client macros/provenance are
        // never trusted; no Recognition/Matcher/Serving/Role-A/Role-B rerun; no legacy perUnit math.
        await mealAPI.rebuildLogAgain({
          source_plan_id: planId,
          date: targetDate,
          meal_type: contextMealType || (meal && meal.meal_type) || 'Snack',
          client_today: liveToday,
          operation_id: logAgainOpRef.current,
        });
        toast.success('Logged again!');
      } else {
        // Fresh log OR "Log Again" → create a brand new entry.
        // Preserve the grouped structure so the re-logged row stays an editable
        // grouped meal (components + FatSecret ids/snapshots + image) and still
        // routes to CustomMealView. Falls back to a flat log for non-grouped meals.
        var structured = ingredients.filter(function(i) { return i.structured || i.food_id; });
        var planData = {
          date: targetDate,
          meal_type: contextMealType || 'Snack',
          meal_id: mealId,
          meal_name: mealName,
          // Keep the grouped source (e.g. AI_PHOTO_LOG) so grouped routing/behaviour is retained.
          source: (meal && meal.source) || 'manual',
          status: 'completed',
          macros: {
            calories: macros.calories,
            protein: macros.protein,
            carbs: macros.carbs,
            fat: macros.fat
          }
        };
        if (structured.length > 0) {
          // NOTE: custom_ingredients is intentionally omitted — the POST /meal-plans
          // model types it as List[Dict] (structured custom-meal shape), while grouped
          // components live in ingredient_details, which get_custom_meal renders from.
          planData.ingredient_details = structured.map(function(i) {
            var comp = {
              name: i.name || (i.text || '').trim(),
              food_id: i.food_id || i.fatsecret_food_id || null,
              quantity: i.quantity || 1,
              unit: i.unit || 'serving',
              calories: Math.round(i.calories || 0),
              protein: Math.round((i.protein || 0) * 10) / 10,
              carbs: Math.round((i.carbs || 0) * 10) / 10,
              fat: Math.round((i.fat || 0) * 10) / 10,
              fatsecret_food_id: i.fatsecret_food_id || i.food_id || null,
              external_id: i.external_id || null,
              match_source: i.match_source || null,
              per_unit: i.per_unit || null,
              serving_description: i.serving_description || null,
              brand: i.brand || null,
              restaurant: i.restaurant || null,
              serving_id: i.serving_id || null,
              serving_size: i.serving_size || null,
              measurement_description: i.measurement_description || null,
              metric_serving_amount: (i.metric_serving_amount != null ? i.metric_serving_amount : null),
              metric_serving_unit: i.metric_serving_unit || null,
            };
            if (i.rebuilt) comp.rebuilt = i.rebuilt;   // ITEM-19: preserve provenance on Log Again
            return comp;
          });
          planData.ingredient_count = structured.length;
          if (imageUrl) planData.image_url = imageUrl;
        }

        await mealPlanAPI.addMealPlan(planData);
        toast.success(isAlreadyLogged ? 'Logged again!' : 'Meal logged!');
      }
      
      navigate(returnTo);
    } catch (err) {
      console.error('[CustomMealView] Error logging meal:', err);
      toast.error('Failed to log meal');
    } finally {
      setLogging(false);
    }
  };

  const getPlaceholderSvg = function() {
    return 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#f3f4f6" width="400" height="300"/><text fill="#9ca3af" font-family="Arial" font-size="48" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">&#127869;</text></svg>');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto" />
          <p className="text-muted-foreground mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={function() { navigate(returnTo); }} className="p-2 -ml-2 rounded-full hover:bg-secondary">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading font-semibold text-lg">Meal Details</h1>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-destructive/50 mx-auto mb-4" />
          <p className="text-muted-foreground">{error}</p>
          <Button variant="outline" className="mt-4" onClick={function() { navigate(returnTo); }}>Go Back</Button>
        </div>
      </div>
    );
  }

  var viewLabel = isSimpleFood ? 'Food Details' : 'Meal Details';
  var typeLabel = isSimpleFood ? 'Logged Food' : 'Custom Meal';

  return (
    <div className="min-h-screen bg-background" data-testid="custom-meal-view">
      {/* Header */}
      <div className="sticky top-0 bg-background border-b border-border z-10 header-safe-top">
        <div className="flex items-center justify-between px-4 pb-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={function() { navigate(returnTo); }} 
              className="p-2 -ml-2 rounded-full hover:bg-secondary"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-heading font-semibold text-lg">
              {editMode ? 'Edit Meal' : viewLabel}
            </h1>
          </div>
          
          {/* Only show Edit for multi-ingredient meals that have real data to edit */}
          {!isSimpleFood && !editMode && (
            <Button variant="outline" size="sm" onClick={function() { setEditMode(true); }} data-testid="edit-button">
              <Edit3 size={16} className="mr-1" />
              Edit
            </Button>
          )}
          {editMode && (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
                <X size={16} className="mr-1" />Cancel
              </Button>
              <Button size="sm" onClick={saveChanges} disabled={saving} data-testid="save-button">
                {saving ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Save size={16} className="mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        {/* Image Section */}
        <div className="relative">
          <div className="aspect-video rounded-xl overflow-hidden bg-secondary">
            <img
              src={heroSrc || getPlaceholderSvg()}
              alt={mealName}
              className="w-full h-full object-cover"
              onError={function(e) { e.target.src = getPlaceholderSvg(); }}
            />
            {uploadingImage && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </div>
          {editMode && (
            <>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <button
                onClick={function() { fileInputRef.current?.click(); }}
                className="absolute bottom-3 right-3 p-3 bg-accent text-white rounded-full shadow-lg hover:bg-accent/90 transition-colors"
                disabled={uploadingImage}
                data-testid="upload-image-button"
              >
                {uploadingImage ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
              </button>
            </>
          )}
        </div>

        {/* Name */}
        <div>
          {editMode ? (
            <input
              type="text"
              value={mealName}
              onChange={function(e) { setMealName(e.target.value); }}
              className="w-full text-2xl font-heading font-bold bg-transparent border-b-2 border-accent focus:outline-none pb-1"
              placeholder="Meal name"
              data-testid="meal-name-input"
            />
          ) : (
            <h2 className="text-2xl font-heading font-bold">{mealName}</h2>
          )}
          <p className="text-sm text-muted-foreground mt-1">{typeLabel}</p>
        </div>

        {/* Macros */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="font-semibold text-sm text-muted-foreground mb-3">NUTRITION</h3>
          <div className="grid grid-cols-4 gap-3">
            {[
              { key: 'calories', label: 'Cal', color: 'text-accent' },
              { key: 'protein', label: 'Protein', suffix: 'g', color: 'text-foreground' },
              { key: 'carbs', label: 'Carbs', suffix: 'g', color: 'text-foreground' },
              { key: 'fat', label: 'Fat', suffix: 'g', color: 'text-foreground' }
            ].map(function(m) {
              return (
                <div key={m.key} className="text-center">
                  {editMode && !isRebuiltMeal ? (
                    <input
                      type="number"
                      value={macros[m.key]}
                      onChange={function(e) { updateMacro(m.key, e.target.value); }}
                      className={'w-full text-center text-2xl font-mono font-bold bg-secondary/50 rounded-lg py-2 ' + m.color}
                      min="0"
                      data-testid={'macro-input-' + m.key}
                    />
                  ) : (
                    <p className={'text-2xl font-mono font-bold ' + m.color}>
                      {macros[m.key]}{m.suffix}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ingredients */}
        {ingredients.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-semibold text-sm text-muted-foreground mb-3">INGREDIENTS</h3>
            <ul className="space-y-2">
              {ingredients.map(function(ing) {
                return (
                  <li key={ing.id} className="flex items-center gap-2" data-testid={'ingredient-row-' + ing.id}>
                    {editMode ? (
                      ing.structured && !ing.rebuilt ? (
                        <StructuredIngredientEditor
                          ing={ing}
                          onQuantityChange={changeIngredientQuantity}
                          onUnitChange={changeIngredientUnit}
                          onRemove={removeIngredient}
                        />
                      ) : ing.rebuilt ? (
                        <div className="flex-1 flex items-center gap-2" data-testid={'rebuilt-locked-' + ing.id}>
                          <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                          <span className="text-sm flex-1">{ing.text}</span>
                          {ing.calories > 0 && <span className="text-xs text-muted-foreground">{ing.calories} cal</span>}
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={ing.text}
                            onChange={function(e) { updateIngredient(ing.id, e.target.value); }}
                            placeholder="e.g., 100g chicken breast"
                            className="flex-1 bg-secondary/50 rounded-lg px-3 py-2.5 text-sm border border-border focus:border-accent focus:ring-1 focus:ring-accent/20 outline-none"
                          />
                          <button onClick={function() { removeIngredient(ing.id); }} className="p-2 text-destructive/70 hover:text-destructive">
                            <Trash2 size={16} />
                          </button>
                        </>
                      )
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                        <span className="text-sm flex-1">{ing.text}</span>
                        {ing.structured && ing.calories > 0 && (
                          <span className="text-xs text-muted-foreground">{ing.calories} cal</span>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            {editMode && !isRebuiltMeal && (
              <button
                onClick={addIngredient}
                className="w-full mt-3 py-2.5 border-2 border-dashed border-accent/40 rounded-lg text-accent hover:bg-accent/5 flex items-center justify-center gap-2"
                data-testid="add-ingredient-button"
              >
                <Plus size={18} />
                <span className="text-sm font-medium">Add Ingredient</span>
              </button>
            )}
          </div>
        )}

        {/* No ingredients — only show for non-simple foods in a helpful way */}
        {ingredients.length === 0 && !isSimpleFood && (
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-semibold text-sm text-muted-foreground mb-3">INGREDIENTS</h3>
            <p className="text-sm text-muted-foreground text-center py-4">
              {editMode ? 'Tap below to add ingredients' : 'No ingredients listed'}
            </p>
            {editMode && (
              <button onClick={addIngredient} className="w-full mt-2 py-2.5 border-2 border-dashed border-accent/40 rounded-lg text-accent hover:bg-accent/5 flex items-center justify-center gap-2">
                <Plus size={18} /><span className="text-sm font-medium">Add Ingredient</span>
              </button>
            )}
          </div>
        )}

        {/* Source info */}
        {meal?.source && (
          <p className="text-xs text-muted-foreground text-center">
            Source: {meal.source === 'CUSTOM' ? 'Custom Meal' : meal.source}
          </p>
        )}

        {/* Log / Log Again Action Button */}
        {!editMode && (
          <div className="space-y-3">
            {isAlreadyLogged && (
              <p className="text-center text-sm text-muted-foreground" data-testid="already-logged-label">
                Already logged on {rowDateLabel}
              </p>
            )}
            <Button
              onClick={handleLogMeal}
              disabled={logging}
              className="w-full h-14 rounded-full bg-accent text-white font-semibold text-lg hover:bg-accent/90"
              data-testid={isAlreadyLogged ? 'log-again-btn' : 'log-meal-btn'}
            >
              {logging ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Logging...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isAlreadyLogged ? <RotateCcw size={20} /> : <Check size={20} />}
                  {isAlreadyLogged ? 'Log Again' : ('Log on ' + logDateLabel)}
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * StructuredIngredientEditor — shows real quantity input and unit dropdown
 * with USDA-backed serving options fetched from the API.
 */
function StructuredIngredientEditor({ ing, onQuantityChange, onUnitChange, onRemove }) {
  const [unitOptions, setUnitOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [canSwitch, setCanSwitch] = useState(false);
  const [triedLoad, setTriedLoad] = useState(false);

  // Fetch full FatSecret serving options (each carries per-1-unit macros + the
  // serving identity/measurement metadata). We reuse the existing endpoint via
  // logFoodAPI.getFoodDetails(food_id). If it can't be loaded/validated, we
  // DISABLE unit switching rather than perform a label-only change.
  const fetchOptions = async function() {
    if (triedLoad || !ing.food_id) return;
    setTriedLoad(true);
    setLoadingOptions(true);
    try {
      var res = await logFoodAPI.getFoodDetails(ing.food_id);
      var item = res && res.data && res.data.item;
      var options = (item && Array.isArray(item.serving_options) ? item.serving_options : [])
        .filter(function(o) { return o && o.unit && o.macros; });
      if (options.length > 0) {
        setUnitOptions(options);
        setCanSwitch(true);
      } else {
        setUnitOptions([]);
        setCanSwitch(false);
      }
    } catch (err) {
      setUnitOptions([]);
      setCanSwitch(false);
    } finally {
      setLoadingOptions(false);
    }
  };

  return (
    <div className="flex-1 flex items-center gap-2">
      <span className="text-sm text-muted-foreground truncate max-w-[100px]" title={ing.name}>{ing.name}</span>
      
      {/* Quantity input */}
      <input
        type="number"
        value={ing.quantity}
        onChange={function(e) {
          var val = parseFloat(e.target.value);
          if (val > 0) onQuantityChange(ing.id, val);
        }}
        className="w-16 text-center text-sm bg-secondary/50 rounded-lg py-1.5 border border-border focus:border-accent outline-none"
        min="0.1"
        step="0.5"
        data-testid={'qty-input-' + ing.id}
      />
      
      {/* Unit dropdown (disabled when serving options can't be loaded/validated) */}
      <div className="relative">
        <button
          onClick={function() {
            if (!showDropdown) fetchOptions();
            setShowDropdown(!showDropdown);
          }}
          className="flex items-center gap-1 text-sm bg-secondary/50 rounded-lg px-2 py-1.5 border border-border hover:border-accent min-w-[80px] justify-between"
          data-testid={'unit-select-' + ing.id}
        >
          <span className="truncate">{ing.unit}</span>
          <ChevronDown size={14} className="flex-shrink-0 text-muted-foreground" />
        </button>
        
        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-border rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto min-w-[120px]">
            {loadingOptions ? (
              <div className="p-2 text-center"><Loader2 size={14} className="animate-spin mx-auto" /></div>
            ) : !canSwitch ? (
              <div className="px-3 py-2 text-xs text-muted-foreground" data-testid={'unit-locked-' + ing.id}>
                Unit options unavailable — keeping “{ing.unit}”.
              </div>
            ) : (
              unitOptions.map(function(opt) {
                return (
                  <button
                    key={opt.unit}
                    onClick={function() { onUnitChange(ing.id, opt); setShowDropdown(false); }}
                    className={'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ' + (opt.unit === ing.unit ? 'bg-accent/5 text-accent font-medium' : '')}
                    data-testid={'unit-option-' + ing.id + '-' + opt.unit}
                  >
                    {opt.unit}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      
      <span className="text-xs text-muted-foreground whitespace-nowrap">{ing.calories} cal</span>
      
      <button onClick={function() { onRemove(ing.id); }} className="p-1.5 text-destructive/70 hover:text-destructive flex-shrink-0">
        <Trash2 size={14} />
      </button>
    </div>
  );
}
